import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Typography, Alert, Spin } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useOnboardingStore } from '../stores/onboarding-store';
import OnboardingWizard from '../components/OnboardingWizard';
import BusinessOnboardingForm from '../components/BusinessOnboardingForm';
import { OnboardingStep } from '../types';
import {useGetCategoryById} from "@/shared/api/client/customer-category-controller";

const { Title, Paragraph } = Typography;

const BusinessOnboardingView: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  
  const {
    selectedCategory,
    setSelectedCategory,
    currentStep,
    steps,
    setSteps,
    setCurrentStep,
    resetOnboarding,
    getCompletionPercentage,
  } = useOnboardingStore();

  const {
    data: category,
    isLoading,
    error,
  } = useGetCategoryById(Number(categoryId), {
    query: {
      enabled: !!categoryId,
    },
  });


  useEffect(() => {
    if (category && (!selectedCategory || selectedCategory.id !== category.id)) {
      setSelectedCategory(category);
      
      // Initialize steps for business onboarding
      const onboardingSteps: OnboardingStep[] = [
        {
          id: 'business-info',
          title: 'Business Information',
          description: 'Basic business details and registration information',
          component: 'BusinessFormFields',
          isRequired: true,
          isCompleted: false,
        },
        {
          id: 'signatories',
          title: 'Signatories',
          description: 'Add authorized signatories for the business',
          component: 'SignatoryManagement',
          isRequired: true,
          isCompleted: false,
        },
        {
          id: 'shareholders',
          title: 'Shareholders',
          description: 'Manage business shareholders and ownership',
          component: 'ShareholderManagement',
          isRequired: false,
          isCompleted: false,
        },
        {
          id: 'directors',
          title: 'Directors',
          description: 'Add company directors and management',
          component: 'DirectorManagement',
          isRequired: false,
          isCompleted: false,
        },
        {
          id: 'products',
          title: 'Product Selection',
          description: 'Select products and services for the business',
          component: 'ProductSelectionForm',
          isRequired: true,
          isCompleted: false,
        },
        {
          id: 'documents',
          title: 'Document Upload',
          description: 'Upload required business documents',
          component: 'DocumentUploadForm',
          isRequired: true,
          isCompleted: false,
        },
        {
          id: 'locations',
          title: 'Address Information',
          description: 'Business address and location details',
          component: 'LocationForm',
          isRequired: true,
          isCompleted: false,
        },
        {
          id: 'review',
          title: 'Review & Submit',
          description: 'Review all information before submission',
          component: 'ReviewForm',
          isRequired: true,
          isCompleted: false,
        },
      ];
      
      setSteps(onboardingSteps);
    }
  }, [category, selectedCategory, setSelectedCategory, setSteps]);

  const handleStepChange = (step: number) => {
    setCurrentStep(step);
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    // Placeholder for business customer submission
    console.log('Business customer onboarding submission:', {
      categoryId,
      category: selectedCategory,
      completionPercentage: getCompletionPercentage(),
      message: 'Business customer onboarding completed successfully'
    });
    
    // Navigate back to category selection after successful submission
    navigate('/customers/onboarding');
    resetOnboarding();
  };

  const handleCancel = () => {
    navigate('/customer-onboarding');
    resetOnboarding();
  };

  if (isLoading) {
    return (
      <div className="onboarding-loading">
        <Spin size="large" />
        <Paragraph>Loading category information...</Paragraph>
      </div>
    );
  }

  if (error || !category) {
    return (
      <div className="onboarding-error">
        <Alert
          message="Category Not Found"
          description="The selected customer category could not be loaded."
          type="error"
          showIcon
          action={
            <Button size="small" onClick={() => navigate('/customer-onboarding')}>
              Back to Categories
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="business-onboarding-view">
      <div className="onboarding-header">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/customer-onboarding')}
          className="back-button"
        >
          Back to Categories
        </Button>
        
        <div className="category-info">
          <Title level={3}>{category.name}</Title>
          <Paragraph>{category.description}</Paragraph>
        </div>
      </div>

      <OnboardingWizard
        steps={steps}
        currentStep={currentStep}
        onStepChange={handleStepChange}
        onNext={handleNext}
        onPrevious={handlePrevious}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        canProceed={true}
      >
        <BusinessOnboardingForm
          categoryId={Number(categoryId)}
          currentStep={currentStep}
        />
      </OnboardingWizard>
    </div>
  );
};

export default BusinessOnboardingView;
